<template>
  <div class="selection-shell">
    <v-btn style="position: fixed; top: 10px; z-index: 5; left: 10px" x-large icon @click="$router.push('/')">
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>

    <v-container>
      <div class="header-section text-center">
        <h1 class="headline mb-2">Wähle dein Erlebnis</h1>
        <p class="sub-headline">Wofür möchtest du dich anmelden?</p>
      </div>

      <div v-if="isLoadingTypes" class="text-center" style="padding: 80px 0">
        <v-progress-circular indeterminate color="var(--v-primary)" size="64"></v-progress-circular>
      </div>

      <div v-else class="type-feed">
        <v-card v-for="type in reservationTypes" :key="type._id" class="type-card" @click="selectType(type)">
          <div class="card-content">
            <div class="card-header">
              <h2 class="card-title">{{ type.name }}</h2>
              <v-chip small class="duration-chip" color="rgba(255,255,255,0.1)">
                <v-icon left small>mdi-clock-outline</v-icon>
                {{ type.timeslotDuration }} min
              </v-chip>
            </div>
            <p class="card-description">{{ type.description }}</p>
          </div>
          <div class="card-action">
            <span>Termin auswählen</span>
            <v-icon right>mdi-arrow-right</v-icon>
          </div>
        </v-card>
        <div v-if="reservationTypes.length === 0" class="no-reservations-placeholder">
          <v-icon class="placeholder-icon">mdi-calendar-search</v-icon>
          <p>Keine Reservierungsmöglichkeiten vorhanden</p>
        </div>
      </div>
      <div class="upcoming-section">
        <h2 class="section-headline">Deine nächsten Termine</h2>
        <div v-if="isLoadingReservations" class="text-center py-8">
          <v-progress-circular indeterminate color="var(--v-primary)" size="40"></v-progress-circular>
        </div>
        <div v-else-if="myReservations.length > 0" class="reservations-list">
          <v-card v-for="rez in myReservations" :key="rez._id" class="reservation-card" :class="{ 'is-cancelled': rez.isCancelled }">
            <div class="rez-icon">
              <v-icon :color="rez.isCancelled ? 'red' : 'var(--v-primary)'">
                {{ rez.isCancelled ? "mdi-calendar-remove-outline" : "mdi-calendar-check" }}
              </v-icon>
            </div>
            <div class="rez-details">
              <span class="rez-title">{{ rez.reservationType.name }}</span>
              <span class="rez-time">{{ formatDateTime(rez.startTime) }}</span>
              <v-chip v-if="rez.isCancelled" x-small color="red" class="mt-1"> Storniert (Defekt) </v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-btn v-if="!rez.isCancelled" icon @click="promptCancelReservation(rez)" class="cancel-btn" title="Termin stornieren">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-card>
        </div>
        <div v-else class="no-reservations-placeholder">
          <v-icon class="placeholder-icon">mdi-calendar-search</v-icon>
          <p>Du hast noch keine Termine gebucht.</p>
        </div>
      </div>
    </v-container>
    <ActionDialog
      :show="showDialog"
      :title="dialog.title"
      :text="dialog.text"
      :confirm-button-text="dialog.confirmText"
      :icon="dialog.icon"
      @close="showDialog = false"
      @confirm="handleDialogConfirm" />
    <upgrade-contract
      :useCustomer="true"
      @hide="showContractDialog = false"
      @created="upgradeCreatedDialog = true"
      :dialog="showContractDialog"></upgrade-contract>
    <v-dialog persistent max-width="500" v-model="upgradeCreatedDialog">
      <v-card dark>
        <v-card-title class="primary white--text">Zusatzpaket Upgrade</v-card-title>
        <v-card-text class="mt-5"
          >Vielen Dank für dein Zusatzpaket Upgrade!
          <br />
          Sobald die Aktivierung abgeschlossen ist, erhältst du eine Bestätigung per E-Mail sowie die entsprechende Rechnung.
          <br /><br />
          Bei Fragen zum Vertrag erreichst du uns unter {{ selectedStudio?.telefon || "" }} oder schreibe uns eine Email an {{
            selectedStudio?.email || "info@wellcomefit.ch"
          }}.
          <br />
          <br />
          Bis bald im {{ selectedStudio?.art?.replace("AG", "") || "well come FIT" }}
        </v-card-text>
        <v-card-actions class="d-flex justify-center">
          <v-btn text @click="upgradeCreatedDialog = false">Alles klar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Der Script-Teil bleibt unverändert, da die Logik dieselbe ist.
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"
import ActionDialog from "../components/ActionDialog.vue"
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract.vue"
export default {
  components: {
    ActionDialog,
    UpgradeContract,
  },
  data() {
    return {
      userService,

      showContractDialog: false,
      upgradeCreatedDialog: false,
      reservationTypes: [],
      myReservations: [],

      isLoadingTypes: true,
      isLoadingReservations: true, // NEU: Eigener Lade-Status

      // NEU: Daten für die Steuerung und Befüllung des Dialogs
      showDialog: false,
      dialog: {
        title: "",
        text: "",
        confirmText: "",
        action: null, // Definiert, was der Bestätigungs-Button auslöst
        icon: "mdi-lock-alert-outline",
        cancellationId: null, // Wichtig, um die ID zu speichern
      },
      isCancelling: false, // Optional, für Ladezustand am Button
    }
  },
  async created() {
    this.fetchReservationTypes()
    this.fetchMyReservations()
  },
  computed: {
    ...mapGetters("userStore", [
      "activeContractDetails", // Zugriff auf den Getter courseRecommendations
    ]),
    ...mapState("studioStore", ["selectedStudio"]),
  },
  methods: {
    promptCancelReservation(reservation) {
      this.dialog = {
        title: "Termin stornieren?",
        text: `Möchtest du den Termin für <strong>${reservation.reservationType.name}</strong> am<br>${this.formatDateTime(
          reservation.startTime
        )} wirklich stornieren?`,
        confirmText: "Ja, stornieren",
        confirmButtonColor: "red", // Machen wir den Button rot für eine destruktive Aktion
        action: "confirmCancel",
        icon: "mdi-trash-can-outline",
        cancellationId: reservation._id, // Die ID der zu löschenden Reservierung merken
      }
      this.showDialog = true
    },
    // NEU: Die eigentliche Logik zum Löschen
    async executeCancellation() {
      if (!this.dialog.cancellationId) return
      this.isCancelling = true

      try {
        await api.reservationTool.deleteReservation(this.dialog.cancellationId)

        // UI sofort aktualisieren für besseres Nutzererlebnis
        this.myReservations = this.myReservations.filter((rez) => rez._id !== this.dialog.cancellationId)
        this.$toast.success("Termin erfolgreich storniert!") // Erfolgsmeldung
        // Optional: Erfolgsmeldung anzeigen (z.B. mit einem Snackbar)
      } catch (error) {
        console.error("Fehler bei der Stornierung:", error)
        // Hier könntest du einen Fehlerdialog anzeigen
      } finally {
        this.isCancelling = false
        this.dialog.cancellationId = null // ID zurücksetzen
      }
    },
    async fetchReservationTypes() {
      this.isLoadingTypes = true
      try {
        const response = await api.reservationTool.getReservationTypes(this.selectedStudio?._id)
        this.reservationTypes = response.data.filter((type) => type.active)
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungstypen:", error)
      } finally {
        this.isLoadingTypes = false
      }
    },
    // NEU: Methode zum Abrufen der eigenen Reservierungen
    async fetchMyReservations() {
      this.isLoadingReservations = true
      try {
        const response = await api.reservationTool.getMyReservations(this.userId)
        this.myReservations = response.data
      } catch (error) {
        console.error("Fehler beim Laden der eigenen Reservierungen:", error)
      } finally {
        this.isLoadingReservations = false
      }
    },
    // In ReservationSelection.vue -> methods

    selectType(type) {
      // Schritt 1: Prüfen, ob für diesen Typ überhaupt eine Mitgliedschaft nötig ist.
      if (!type.membersOnly) {
        // Wenn nicht, direkt zur Buchung weiterleiten.
        this.navigateToBooking(type)
        return
      }

      // Schritt 2: Sicherstellen, dass der Nutzer überhaupt einen aktiven Vertrag hat.
      if (!this.activeContractDetails) {
        this.showSubscriptionNeededDialog()
        return
      }

      // Schritt 3: Die benötigten Abo- und Paket-Titel aus den Regeln extrahieren.
      // Wir konvertieren sie direkt in Kleinbuchstaben für einen robusten Vergleich.
      const requiredSubs = (type.requiredSubscriptionTypes || []).map((s) => s.toLowerCase())
      const requiredAddons = (type.requiredAddonPackages || []).map((p) => p.toLowerCase())

      // Schritt 4: Falls keine spezifischen Abos ODER Pakete gefordert sind,
      // aber `membersOnly` aktiv ist, reicht ein beliebiger aktiver Vertrag.
      if (requiredSubs.length === 0 && requiredAddons.length === 0) {
        this.navigateToBooking(type)
        return
      }

      // Schritt 5: Die Titel des Nutzers aus dem Vuex-Getter holen.
      const userSubscriptionTitle = (this.activeContractDetails.title || "").toLowerCase()
      const userActiveAddons = this.activeContractDetails.activeAddonsTitles.map((e) => e.toLowerCase()) || []

      // Mitarbeiter sollten immer Zugriff haben, daher prüfen wir das hier.
      if (
        userSubscriptionTitle.includes("mitarbeiter") ||
        ((userSubscriptionTitle.includes("erwachsene") ||
          userSubscriptionTitle.includes("ahv") ||
          (userSubscriptionTitle.includes("jahres abo") && !userSubscriptionTitle.includes("kombi"))) &&
          !requiredSubs.some((reqSub) => reqSub.includes("pelvi"))) ||
        (userSubscriptionTitle.includes("premium") && !requiredSubs.some((reqSub) => reqSub.includes("pelvi")))
      ) {
        this.navigateToBooking(type)
        return
      }

      // Schritt 6: Die Berechtigung prüfen (Core-Logik)
      let hasPermission = false

      // Prüfung 6a: Hat der Nutzer ein passendes Abo?
      // Wir nutzen .some(), um zu prüfen, ob mindestens eine Anforderung erfüllt ist.
      if (requiredSubs.length > 0) {
        hasPermission = requiredSubs.some((reqSub) => userSubscriptionTitle.includes(reqSub))
      }

      // Prüfung 6b: Hat der Nutzer ein passendes Zusatzpaket?
      // Nur prüfen, wenn die Abo-Prüfung fehlgeschlagen ist.
      if (!hasPermission && requiredAddons.length > 0) {
        hasPermission = requiredAddons.some((reqAddon) =>
          // Prüft, ob einer der aktiven Paket-Titel des Nutzers die Anforderung enthält.
          userActiveAddons.some((userAddon) => userAddon.toLowerCase().includes(reqAddon))
        )
      }

      // Schritt 7: Finale Entscheidung basierend auf der Berechtigung
      if (hasPermission) {
        this.navigateToBooking(type)
      } else {
        // Wenn keine Berechtigung gefunden wurde, zeige die Fehlermeldung.
        this.showUpgradeNeededDialog()
      }
    },

    showSubscriptionNeededDialog() {
      this.dialog = {
        title: "Aktives Abo benötigt",
        text: "Für diese Buchung wird ein aktives Abo benötigt.<br>Bitte löse zuerst ein Abo, um fortzufahren.",
        confirmText: "Abo lösen",
        action: "navigateToContracts",
        icon: "mdi-star-circle-outline",
      }
      this.showDialog = true
    },

    showUpgradeNeededDialog() {
      this.dialog = {
        title: "Zusatzpaket erforderlich",
        text: "Für diese Buchung wird ein Zusatzpaket benötigt.<br>Bitte löse zuerst das Zusatzpaket, um fortzufahren.",
        confirmText: "zu den Upgrades",
        action: "navigateToUpgrades",
        icon: "mdi-arrow-up-bold-circle-outline",
      }
      this.showDialog = true
    },

    // WIR ERWEITERN DEINEN BESTEHENDEN DIALOG-HANDLER
    handleDialogConfirm() {
      // Die Logik für Abo- und Upgrade-Dialoge bleibt
      if (this.dialog.action === "navigateToContracts") {
        this.$router.push("/contract?studio=" + this.selectedStudio?.code)
      } else if (this.dialog.action === "navigateToUpgrades") {
        this.showContractDialog = true
      }
      // NEUE Logik für die Stornierung
      else if (this.dialog.action === "confirmCancel") {
        this.executeCancellation()
      }

      // Dialog immer schliessen
      this.showDialog = false
    },

    // NEU: Eine kleine Hilfsmethode, um Code-Wiederholung zu vermeiden.
    navigateToBooking(type) {
      this.$router.push({
        name: "ReservationBooking",
        params: { reservationTypeId: type._id },
      })
    },
    // NEU: Hilfsfunktion zur Formatierung von Datum und Zeit
    formatDateTime(dateTime) {
      return this.$moment.utc(dateTime).format("dd, DD.MM.YY [um] HH:mm [Uhr]")
    },
  },
}
</script>

<style scoped>
.reservation-card {
  display: flex; /* Wichtig, damit der Spacer funktioniert */
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}

/* NEU */
.cancel-btn {
  color: var(--text-secondary) !important;
}

.cancel-btn:hover {
  color: #ff5252 !important; /* Rote Farbe beim Hovern */
}
.selection-shell {
  --primary: #ff0055;
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;
  border-radius: 0px !important;
  background-color: var(--bg);
  min-height: 100vh;
  padding-top: 40px;
  padding-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* --- NEU: STYLING FÜR KOMMENDE TERMINE --- */
.upcoming-section {
  max-width: 600px;
  margin: 60px auto 60px auto; /* Abstand nach unten */
}
.section-headline {
  font-size: 1.5rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-align: center;
}
.reservations-list {
  display: grid;
  gap: 12px;
}
.reservation-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}
.rez-icon {
  margin-right: 16px;
}
.rez-details {
  display: flex;
  flex-direction: column;
}

/* NEUER STYLE für stornierte Karten */
.is-cancelled {
  opacity: 0.6;
  background: repeating-linear-gradient(45deg, var(--card-bg), var(--card-bg) 10px, #2a2a2a 10px, #2a2a2a 20px) !important;
}

.is-cancelled .rez-title {
  text-decoration: line-through;
}
.rez-title {
  font-weight: 600;
  color: var(--text-primary);
}
.rez-time {
  font-size: 0.9rem;
  color: var(--text-secondary);
}
.no-reservations-placeholder {
  text-align: center;
  padding: 30px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 2px dashed var(--border);
}
.placeholder-icon {
  font-size: 3rem !important;
  color: var(--text-secondary) !important;
  margin-bottom: 12px;
}
.no-reservations-placeholder p {
  color: var(--text-secondary);
  font-weight: 500;
}

/* --- Bestehendes Styling (wie zuvor) --- */
.header-section {
  margin-bottom: 40px;
}
.headline {
  font-size: clamp(2rem, 6vw, 2.5rem);
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}
.sub-headline {
  font-size: 1.1rem;
  color: var(--text-secondary);
}
.type-feed {
  display: grid;
  gap: 20px;
  max-width: 600px;
  margin: auto;
}
.type-card {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border-radius: 20px !important;
  border: 1px solid var(--border);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* --- THEME & TikTok-Style --- */
.selection-shell {
  --primary: #ff0055; /* Ein kräftiges Pink/Magenta */
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;

  background-color: var(--bg);
  min-height: 100vh;
  padding-top: 40px;
  padding-bottom: 40px;
}

/* --- HEADER --- */
.header-section {
  margin-bottom: 40px;
}
.headline {
  font-size: clamp(2rem, 6vw, 2.5rem);
  font-weight: 800;
  color: var(--text-primary);
}
.sub-headline {
  font-size: 1.1rem;
  color: var(--text-secondary);
}

/* --- TYPE FEED LAYOUT --- */
.type-feed {
  display: grid;
  gap: 20px;
  max-width: 600px;
  margin: auto;
}

/* --- TYPE CARD STYLING --- */
.type-card {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border-radius: 20px !important;
  border: 1px solid var(--border);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  overflow: hidden; /* Wichtig für den Action-Bereich */
  display: flex;
  flex-direction: column;
}

.type-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.card-content {
  padding: 24px;
  flex-grow: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-title {
  font-size: 1.4rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}

.duration-chip {
  background-color: rgba(255, 255, 255, 0.05) !important;
  color: var(--text-secondary) !important;
  font-weight: 500;
}

.card-description {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

/* --- CARD ACTION FOOTER --- */
.card-action {
  background-color: var(--v-primary);
  color: var(--text-primary);
  padding: 16px 24px;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.type-card:hover .card-action {
  background-color: var(--v-primary); /* Etwas helleres Pink beim Hover */
}

.card-action .v-icon {
  transition: transform 0.3s ease;
}

.type-card:hover .card-action .v-icon {
  transform: translateX(5px);
}
</style>
